import React, { useEffect, useRef, useState } from "react";
import { ArrowUpTrayIcon, XMarkIcon } from "@heroicons/react/24/outline";

export default function RepresentativeDocumentsTab({ repId, repUserId }) {
  const [documents, setDocuments] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const accept = ".pdf,.png,.jpg,.jpeg";

  useEffect(() => {
    if (repId) fetchDocuments();
  }, [repId]);

  const fetchDocuments = async () => {
    const r = await fetch(
      `${baseUrl}/api/Representative/${repUserId}/documents`
    );
    if (r.ok) setDocuments(await r.json());
  };

  const openPicker = () => inputRef.current?.click();

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) setSelectedFile(f);
  };
  const onDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };
  const onDragLeave = () => setDragOver(false);
  const onFileChange = (e) => setSelectedFile(e.target.files?.[0] ?? null);
  const clearSelection = () => {
    setSelectedFile(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setProgress(20);
    const fd = new FormData();
    fd.append("file", selectedFile);
    const r = await fetch(
      `${baseUrl}/api/Representative/${repId}/upload-document`,
      { method: "POST", body: fd }
    );
    setProgress(90);
    if (r.ok) {
      try {
        const newDoc = await r.json();
        if (newDoc?.id) setDocuments((p) => [newDoc, ...p]);
        else await fetchDocuments();
      } catch {
        await fetchDocuments();
      }
      clearSelection();
    } else {
      alert("Erreur lors de l'envoi du fichier.");
    }
    setProgress(100);
    setTimeout(() => {
      setUploading(false);
      setProgress(0);
    }, 300);
  };

  return (
    <div className="text-gray-700">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Gauche : dropzone clickable */}
        <div>
          <h3 className="font-semibold mb-3">Ajouter un document</h3>

          <div
            role="button"
            tabIndex={0}
            onClick={openPicker}
            onKeyDown={(e) =>
              (e.key === "Enter" || e.key === " ") && openPicker()
            }
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            className={[
              "rounded-2xl border-2 border-dashed p-6 transition-all cursor-pointer select-none",
              "bg-white/70 shadow-sm",
              dragOver
                ? "border-[#94d3cc] bg-[#f0fffd] ring-2 ring-[#94d3cc]/40"
                : "border-gray-300 hover:border-papayaTitle/70 hover:bg-white",
            ].join(" ")}
          >
            <input
              ref={inputRef}
              type="file"
              accept={accept}
              onChange={onFileChange}
              className="hidden"
            />

            <div className="flex flex-col items-center text-center gap-3">
              <div className="h-12 w-12 rounded-full bg-papayaTitle/20 flex items-center justify-center shadow">
                <ArrowUpTrayIcon className="h-6 w-6 text-papayaTitle" />
              </div>

              <div className="text-base">
                <span className="font-medium">
                  Glissez-déposez un fichier ici
                </span>
                <div className="text-sm text-gray-500">
                  ou cliquez pour sélectionner
                </div>
              </div>

              <div className="text-xs text-gray-500">
                Extensions acceptées :{" "}
                <span className="font-medium">{accept}</span>
              </div>

              {selectedFile && (
                <div className="mt-4 w-full max-w-md">
                  <div className="flex items-center justify-between rounded-xl border bg-white px-3 py-2 shadow-sm">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">
                        {selectedFile.name}
                      </div>
                      <div className="text-xs text-gray-500">
                        {(selectedFile.size / 1024).toFixed(0)} Ko
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearSelection();
                      }}
                      className="p-1 hover:bg-gray-100 rounded-lg"
                      aria-label="Retirer le fichier"
                      title="Retirer le fichier"
                    >
                      <XMarkIcon className="h-5 w-5 text-gray-500" />
                    </button>
                  </div>

                  {uploading && (
                    <div className="mt-2 h-2 w-full rounded-full bg-gray-200 overflow-hidden">
                      <div
                        className="h-2 bg-papayaTitle transition-all"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  )}
                </div>
              )}

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onUpload();
                }}
                disabled={!selectedFile || uploading}
                className="flex items-center bg-papayaTitle text-white px-5 py-2 rounded-full shadow hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? "Téléversement..." : "Téléverser"}
              </button>
            </div>
          </div>
        </div>

        {/* Droite : liste */}
        <div>
          <h3 className="font-semibold mb-3">Documents disponibles</h3>
          {documents.length === 0 ? (
            <p className="text-gray-600">
              Aucun document pour ce représentant.
            </p>
          ) : (
            <ul className="space-y-2">
              {documents.map((doc, idx) => (
                <li
                  key={idx} // backend ne renvoie pas d'id, fallback avec index
                  className="flex justify-between items-center rounded-xl border bg-white px-3 py-2 shadow-sm"
                >
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline truncate"
                    title={doc.fileName}
                  >
                    {doc.fileName}
                  </a>
                  {doc.uploadedAt && (
                    <span className="ml-3 shrink-0 text-sm text-gray-500">
                      {new Date(doc.uploadedAt).toLocaleDateString()}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
